import jp from 'jsonpath';
import type {
  EntityRootData,
  ConfigEntityRootData,
  EntityDetailRelationships,
  AnnotatedJsonSchema,
  EntityRelationship,
  EntityDetailSchemaWithRelationships,
  RelationshipEntityReferenceAnnotation,
} from '@web-config-app/core';
import {
  findSchemaPathsWhere,
  isRelationshipEntityReference,
  convertSchemaPathToJsonPath,
  getResolvedSchemaNodeAtPath,
  getRelationshipSchemaItemType,
  getRelationshipDataSchema,
  isRelationshipArrayDataSchema,
} from '@web-config-app/schema-utils';

interface RelationshipSyncInfo {
  /** All relationship names found in x-entity-reference annotations */
  annotatedNames: Set<string>;
  /** Relationship names marked with `injected: true` (schema-only, no data) */
  injectedNames: Set<string>;
  /** Entity IDs grouped by relationship name, preserving duplicates */
  refsByRelationship: Record<string, string[]>;
}

/**
 * Single-pass traversal that collects everything `syncRelationshipsFromSchema`
 * needs: annotated relationship names, injected names, and the actual ID
 * references from the entity data.
 *
 * This replaces the previous two-traversal approach (getAnnotatedRelationshipNames
 * + getEntityReferenceIds) with one `findSchemaPathsWhere` walk that captures
 * annotation metadata during traversal and extracts data IDs from the found paths.
 */
function getRelationshipSyncInfo(
  data: EntityRootData | ConfigEntityRootData,
  schema: AnnotatedJsonSchema,
): RelationshipSyncInfo {
  const annotatedNames = new Set<string>();
  const injectedNames = new Set<string>();
  const refsByRelationship: Record<string, string[]> = {};

  // Single traversal: find all x-entity-reference annotations with a
  // `relationship` property. The predicate captures annotation metadata
  // (names + injected) as a side effect, and the returned paths are used
  // below to extract IDs from the data.
  const refSchemaPaths = findSchemaPathsWhere(schema, (schemaNode) => {
    const ref = schemaNode['x-entity-reference'];
    if (isRelationshipEntityReference(ref)) {
      const relRef = ref as RelationshipEntityReferenceAnnotation;
      annotatedNames.add(relRef.relationship);
      if (relRef.injected) {
        injectedNames.add(relRef.relationship);
      }
      return true;
    }
    return false;
  });

  // Extract IDs from entity data using the found schema paths
  refSchemaPaths.forEach((schemaPath) => {
    const jsonPath = convertSchemaPathToJsonPath(schemaPath);
    const nodes = jp.nodes(data, jsonPath);

    nodes.forEach(({ path: nodePath, value }) => {
      const dataPath = nodePath.slice(1).join('.');
      const resolvedSchemaNode = getResolvedSchemaNodeAtPath(
        dataPath,
        schema,
        data,
      );
      if (!resolvedSchemaNode) return;

      const entityReference = resolvedSchemaNode['x-entity-reference'];
      if (!entityReference || !isRelationshipEntityReference(entityReference))
        return;

      const relRef = entityReference as RelationshipEntityReferenceAnnotation;

      // Handle relationshipDataIdKey (object-valued refs like relatedCampaigns)
      if (
        typeof value === 'object' &&
        value !== null &&
        relRef.relationshipDataIdKey
      ) {
        const extractedId = (value as Record<string, unknown>)[
          relRef.relationshipDataIdKey
        ];
        if (typeof extractedId === 'string' && extractedId.length > 0) {
          const ids = refsByRelationship[relRef.relationship] ?? [];
          ids.push(extractedId);
          refsByRelationship[relRef.relationship] = ids;
        }
        return;
      }

      if (typeof value !== 'string' || value.length === 0) return;

      const ids = refsByRelationship[relRef.relationship] ?? [];
      ids.push(value);
      refsByRelationship[relRef.relationship] = ids;
    });
  });

  return { annotatedNames, injectedNames, refsByRelationship };
}

/**
 * Synchronizes the entity's `relationships` object with the actual entity reference
 * values found in the form data based on schema annotations.
 *
 * For each relationship that has at least one corresponding `x-entity-reference`
 * annotation with a `relationship` property in the entity schema, this function
 * rebuilds the relationship entries to match exactly what's in the data — removing
 * stale entries (e.g. from deleted activity sets) and preserving legitimate
 * duplicates (e.g. the same activity referenced in multiple activity sets).
 *
 * Relationships that have no corresponding `x-entity-reference` annotations in the
 * schema are preserved as-is, since they may be managed externally.
 */
export const syncRelationshipsFromSchema = <
  T extends EntityRootData | ConfigEntityRootData | undefined,
>(
  data: T,
  schema: AnnotatedJsonSchema,
  /**
   * Optional separate relationships schema. When the form schema
   * (`schema`) lacks a `properties.relationships` definition (e.g. for
   * ConfigEntity where relationships live on the domain schema), pass
   * the relationships schema here instead of merging schemas.
   */
  relationshipsSchema?: AnnotatedJsonSchema,
): T => {
  if (!data) return data;

  const relationshipsSchemaProperties = (
    (relationshipsSchema ?? schema.properties?.relationships) as
      | AnnotatedJsonSchema
      | undefined
  )?.properties;

  // No relationship schema defined — nothing to sync
  if (!relationshipsSchemaProperties) {
    return data;
  }

  // Single-pass traversal: collects annotated relationship names, injected
  // names, and grouped data IDs in one schema walk.
  const { annotatedNames, injectedNames, refsByRelationship } =
    getRelationshipSyncInfo(data, schema);

  if (annotatedNames.size === 0) {
    return data;
  }

  const existingRelationships = data.relationships ?? {};
  const newRelationships: EntityDetailRelationships = {
    ...existingRelationships,
  };

  // Build a minimal schema wrapper so we can pass it to the shared
  // `getRelationshipSchemaItemType` / `getRelationshipDataSchema` helpers
  // which expect `EntityDetailSchemaWithRelationships`.
  const relationshipsSchemaWrapper = {
    properties: {
      relationships: { properties: relationshipsSchemaProperties },
    },
  } as EntityDetailSchemaWithRelationships;

  Array.from(annotatedNames).forEach((relationshipName) => {
    // If the relationship isn't defined in the relationships schema, skip it
    if (!relationshipsSchemaProperties[relationshipName]) return;

    // Injected relationships (from x-entity-inject-reference) have a
    // schema-only data property that never exists in API responses.
    // The relationships object is the sole source of truth — skip syncing
    // so we don't clear valid entries based on absent data.
    if (injectedNames.has(relationshipName)) {
      return;
    }

    const ids = refsByRelationship[relationshipName] ?? [];
    const { type: schemaItemType } = getRelationshipSchemaItemType(
      relationshipName,
      relationshipsSchemaWrapper,
    );
    const entityType = schemaItemType || 'config-entity';
    const dataSchema = getRelationshipDataSchema(
      relationshipName,
      relationshipsSchemaWrapper,
    );

    if (isRelationshipArrayDataSchema(dataSchema)) {
      newRelationships[relationshipName] = {
        data: ids.map(
          (id): EntityRelationship => ({
            id,
            type: entityType,
          }),
        ),
      };
    } else if (ids.length > 0) {
      newRelationships[relationshipName] = {
        data: { id: ids[0], type: entityType },
      };
    } else {
      // Single relationship with no references — remove it
      delete newRelationships[relationshipName];
    }
  });

  return { ...data, relationships: newRelationships } as T;
};
